//
//
//
//
//
//


import * as resorts from '~/config/ids/idsResorts';
import { PRE_SEASON } from '~/config/settings/settingsPreSeason';
import loadMapOverlay from '~/mixins/components/loadMapOverlay';
import { RESTAURANTS_HIKING, RESTAURANTS_NOT_SKIING, RESTAURANTS_TOBOGGANING } from '~/config/settings/settingsActivityModes';

/**
 * Represents a map to be included into the jungfrau.ch iframe view
 */
export default {

    mixins: [
        loadMapOverlay,
    ],

    data() {
        return {
            isMapOverlayVisible: false,
        };
    },

    computed: {
        tracks() { return this.$store.state.apidata.resortData.tracks || []; },
        lifts() { return this.$store.state.apidata.resortData.lifts || []; },
        pois() { return this.$store.state.apidata.resortData.pois || []; },

        mapClasses() {
            const activityModeClasses = this.$store.getters['activityModes/items']
                .filter((item) => item.isSelected).map((item) => `map--activity-mode-${item.name}`);

            return [
                ...activityModeClasses,
                `map--${this.$store.state.currentSeason}`,
            ];
        },

        backgroundURL() {
            if (this.$store.state.isWebPCheckFinished) {
                const imageName = this.$store.state.currentResortId || `${this.$store.state.currentSeason}_overview`;
                return `${this.$router.options.base}images/backgrounds/${this.$store.state.currentSeason}/${imageName}.${this.$store.state.isWebPEnabled ? 'webp' : 'jpg'}`;
            }
            return '';
        },

        overlayURL() {
            const imageName = this.$store.state.currentResortId || `${this.$store.state.currentSeason}_overview`;
            return `${this.$router.options.base}images/overlays/${this.$store.state.currentSeason}/online/${imageName}.svg`;
        },

        isPreSeason() {
            if (this.$store.state.currentResortId === null) {
                return false;
            }
            return PRE_SEASON[this.$store.state.currentResortId];
        },
    },

    async mounted() {
        await this.loadMapOverlay();
        await this.$nextTick();

        if (this.overlay) {
            this.applyActivityMode();
            this.initMapStatus();
        }
    },

    methods: {

        /**
         * Highlight tracks on map to match API status
         */
        initMapStatus() {
            this.lifts.forEach((lift) => {
                const liftEl = this.overlay.querySelector(`#resort_${lift.sismediaResortId}_lift_${lift.sismediaLiftId}`);
                if (liftEl) {
                    liftEl.classList.add('lift');
                    if (lift.status) {
                        liftEl.classList.remove('closed', 'open', 'unavailable', 'restricted', 'in_preparation');
                        liftEl.classList.add(lift.status.toLowerCase());
                    }

                    if ((lift.isPreSeasonDisabled && this.isPreSeason)
                        || (this.$store.state.currentResortId === null
                            && ((lift.sismediaResortId === '1' && PRE_SEASON[resorts.WINTER_GW] && lift.isPreSeasonDisabled)
                             || (lift.sismediaResortId === '2' && PRE_SEASON[resorts.WINTER_MS] && lift.isPreSeasonDisabled)
                             || (lift.sismediaResortId === '3' && PRE_SEASON[resorts.WINTER_GF] && lift.isPreSeasonDisabled)))) {
                        liftEl.classList.add('pre-season-status-disabled');
                    }

                    if (lift.isHiking) { liftEl.classList.add('lift--hiking'); }
                    if (lift.isTobogganing) { liftEl.classList.add('lift--tobogganing'); }
                    if (lift.isNotSkiing) { liftEl.classList.add('lift--not-skiing'); }
                }

                const railwayEl = this.overlay.querySelector(`#resort_${lift.sismediaResortId}_rail_${lift.sismediaLiftId}`);
                if (railwayEl) {
                    railwayEl.classList.add('railway');

                    if (lift.isHiking) { railwayEl.classList.add('railway--hiking'); }
                    if (lift.isTobogganing) { railwayEl.classList.add('railway--tobogganing'); }
                    if (lift.isNotSkiing) { railwayEl.classList.add('railway--not-skiing'); }

                    if (lift.status) {
                        railwayEl.classList.remove('closed', 'open', 'unavailable', 'restricted', 'in_preparation');
                        railwayEl.classList.add(lift.status.toLowerCase());
                    }
                }
                const buslineEl = this.overlay.querySelector(`#resort_${lift.sismediaResortId}_bus_${lift.sismediaLiftId}`);
                if (buslineEl) {
                    buslineEl.classList.add('busline');

                    if (lift.isHiking) { buslineEl.classList.add('busline--hiking'); }
                    if (lift.isTobogganing) { buslineEl.classList.add('busline--tobogganing'); }
                    if (lift.isNotSkiing) { buslineEl.classList.add('busline--not-skiing'); }

                    if (lift.status) {
                        buslineEl.classList.remove('closed', 'open', 'unavailable', 'restricted', 'in_preparation');
                        buslineEl.classList.add(lift.status.toLowerCase());
                    }
                }
            });

            this.tracks.forEach((track) => {
                const trackEl = this.overlay.querySelector(`#resort_${track.sismediaResortId}_track_${track.sismediaTrackId}`);
                if (trackEl) {
                    trackEl.classList.add('track');

                    if (track.isHiking) { trackEl.classList.add('track--hiking'); }
                    if (track.isTobogganing) { trackEl.classList.add('track--tobogganing'); }

                    if (track.status) {
                        trackEl.classList.remove('closed', 'open', 'unavailable', 'restricted', 'in_preparation');
                        trackEl.classList.add(track.status.toLowerCase());
                        // Move open tracks to the end of the tracks group, so open tracks are visually always
                        // above closed tracks, if they overlap
                        if (track.status !== 'OPEN') {
                            trackEl.parentNode.prepend(trackEl);
                        }
                    }
                    if (track.trackType) trackEl.classList.add(track.trackType.toLowerCase());
                }
            });

            this.pois.forEach((poi) => {
                const poiEl = this.overlay.querySelector(`#resort_${poi.sismediaResortId}_poi_${poi.sismediaPoiId}`);
                if (poiEl) {
                    poiEl.classList.add('poi');

                    if (poi.isHiking) { poiEl.classList.add('poi--hiking'); }
                    if (poi.isTobogganing) { poiEl.classList.add('poi--tobogganing'); }
                    if (poi.isNotSkiing) { poiEl.classList.add('poi--not-skiing'); }

                    // add poi type
                    if (poi.poiType) poiEl.classList.add(poi.poiType.toLowerCase());
                }
            });

            this.overlay.querySelectorAll('[id*="_restaurant_"]').forEach((restaurantEl) => {
                if (restaurantEl) {
                    restaurantEl.classList.add('restaurant');
                    if (RESTAURANTS_HIKING.includes(restaurantEl.id)) { restaurantEl.classList.add('restaurant--hiking'); }
                    if (RESTAURANTS_TOBOGGANING.includes(restaurantEl.id)) { restaurantEl.classList.add('restaurant--tobogganing'); }
                    if (RESTAURANTS_NOT_SKIING.includes(restaurantEl.id)) { restaurantEl.classList.add('restaurant--not-skiing'); }
                }
            });

            const eigerExpressOpen = this.overlay.querySelector('#resort_1_lift_600.open');
            const ggmOpen = this.overlay.querySelector('#resort_1_lift_15.open');

            if (eigerExpressOpen) {
                const cabinImage = document.createElementNS('http://www.w3.org/2000/svg', 'image');
                cabinImage.setAttributeNS('http://www.w3.org/1999/xlink', 'href', `${this.$router.options.base}images/liftcabins/eigerexpress.png`);
                if (eigerExpressOpen.querySelector('#icon')) eigerExpressOpen.querySelector('#icon').appendChild(cabinImage);
            }

            if (ggmOpen) {
                const cabinImage = document.createElementNS('http://www.w3.org/2000/svg', 'image');
                cabinImage.setAttributeNS('http://www.w3.org/1999/xlink', 'href', `${this.$router.options.base}images/liftcabins/ggm.png`);
                if (ggmOpen.querySelector('#icon')) ggmOpen.querySelector('#icon').appendChild(cabinImage);
            }

            this.isMapOverlayVisible = true;
        },

        applyActivityMode() {
            if (this.$route.query.mode) {
                const item = this.$store.getters['activityModes/items'].find((i) => i.name === this.$route.query.mode);
                if (item) this.$store.dispatch('activityModes/selectItem', item);
            }

            if (!this.$route.query.mode && this.$route.name.includes('overview')) {
                const item = this.$store.getters['activityModes/items'].find((i) => i.name === 'skiing');
                if (item) this.$store.dispatch('activityModes/selectItem', item);
            }
        },
    },
};

